import { useEffect, useRef, useState } from "react"
import { useMutation } from "react-apollo"
import classNames from "classnames"
import { prepareImage } from "../onboarding/photo-cropper"
import { COVER_PATTERNS, COVER_TONES, resolveCover } from "./lib"
import { UPLOAD_COVER_PHOTO } from "./queries"
import { IconImage, IconUpload } from "./icons"
import { Modal, Spinner, errorText, useSaveSection } from "./ui"

const PatternArt = ({ id }) => {
  if (id === "arches") {
    const arches = [0, 1, 2, 3, 4, 5, 6, 7]
    return (
      <svg width="100%" height="100%" viewBox="0 0 1200 220" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
        {arches.map((i) => {
          const x = i * 150
          const top = i % 2 ? 150 : 120
          return (
            <path
              key={i}
              d={`M${x + 20} 220 V${top} A55 55 0 0 1 ${x + 130} ${top} V220 Z`}
              fill={i % 2 ? "#AFCBAA" : "#C5D9C1"}
            />
          )
        })}
      </svg>
    )
  }
  if (id === "hills") {
    return (
      <svg width="100%" height="100%" viewBox="0 0 1200 220" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
        <circle cx="1010" cy="62" r="26" fill="#EFCF9C" />
        <circle cx="180" cy="340" r="240" fill="#E8D5BA" />
        <circle cx="640" cy="370" r="280" fill="#E8D5BA" />
        <circle cx="1090" cy="330" r="230" fill="#E8D5BA" />
        <circle cx="420" cy="400" r="230" fill="#DCC29E" />
        <circle cx="900" cy="410" r="240" fill="#DCC29E" />
      </svg>
    )
  }
  return (
    <svg width="100%" height="100%" viewBox="0 0 1200 220" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
      <rect x="-40" y="34" width="420" height="34" rx="17" fill="#C6DBF7" />
      <rect x="300" y="92" width="520" height="34" rx="17" fill="#AFCBF2" />
      <rect x="760" y="34" width="380" height="34" rx="17" fill="#C6DBF7" />
      <rect x="60" y="150" width="300" height="34" rx="17" fill="#AFCBF2" />
      <rect x="480" y="150" width="260" height="34" rx="17" fill="#C6DBF7" />
      <rect x="880" y="150" width="420" height="34" rx="17" fill="#C6DBF7" />
      <rect x="1000" y="92" width="240" height="34" rx="17" fill="#AFCBF2" />
    </svg>
  )
}

// The band's contents for a resolved cover
export const CoverArt = ({ cover }) => {
  if (cover.kind === "photo") return <img className="fp-cover-photo" src={cover.photo} alt="" />
  if (cover.kind === "pattern") return <PatternArt id={cover.id} />
  return null
}

export const CoverBand = ({ cover, children }) => (
  <div className="fp-cover" style={{ backgroundColor: cover.color || "#E7E5E4" }}>
    <CoverArt cover={cover} />
    {children}
  </div>
)

// Frame a cover photo: drag to move it inside the 4:1 band
const CoverPhotoEditor = ({ image, onCancel, onSaved }) => {
  const frame = useRef(null)
  const drag = useRef(null)
  const [size, setSize] = useState(null) // frame { w, h }
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [upload] = useMutation(UPLOAD_COVER_PHOTO)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  const scaleFor = (s) => Math.max(s.w / image.width, s.h / image.height)
  const clampOffset = (x, y, s) => {
    const k = scaleFor(s)
    return {
      x: Math.min(0, Math.max(s.w - image.width * k, x)),
      y: Math.min(0, Math.max(s.h - image.height * k, y)),
    }
  }

  useEffect(() => {
    const w = (frame.current && frame.current.clientWidth) || 600
    const s = { w, h: w / 4 }
    const k = scaleFor(s)
    setSize(s)
    setOffset({ x: (s.w - image.width * k) / 2, y: (s.h - image.height * k) / 2 })
  }, [image.src])

  const onPointerDown = (e) => {
    drag.current = { px: e.clientX, py: e.clientY, x: offset.x, y: offset.y }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e) => {
    if (!drag.current || !size) return
    const d = drag.current
    setOffset(clampOffset(d.x + e.clientX - d.px, d.y + e.clientY - d.py, size))
  }
  const endDrag = () => {
    drag.current = null
  }
  const onKeyDown = (e) => {
    const moves = { ArrowUp: [0, 12], ArrowDown: [0, -12], ArrowLeft: [12, 0], ArrowRight: [-12, 0] }
    if (!moves[e.key] || !size) return
    e.preventDefault()
    setOffset(clampOffset(offset.x + moves[e.key][0], offset.y + moves[e.key][1], size))
  }

  const save = async () => {
    if (!size) return
    const k = scaleFor(size)
    const crop = {
      x: -offset.x / k / image.width,
      y: -offset.y / k / image.height,
      width: size.w / k / image.width,
      height: size.h / k / image.height,
    }
    setBusy(true)
    setError("")
    try {
      await upload({ variables: { dataUrl: image.src, crop } })
      onSaved()
    } catch (e) {
      setBusy(false)
      setError(errorText(e))
    }
  }

  const k = size ? scaleFor(size) : 1
  return (
    <Modal title="Frame your cover" titleId="fp-cover-frame" onClose={onCancel} wide>
      <div
        ref={frame}
        className="fp-cover-frame"
        role="img"
        aria-label="Your cover photo. Drag or use the arrow keys to move it."
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        {size && (
          <img
            src={image.src}
            alt=""
            draggable={false}
            style={{
              width: image.width * k,
              height: image.height * k,
              transform: `translate(${offset.x}px, ${offset.y}px)`,
            }}
          />
        )}
      </div>
      <p className="fp-hint">Drag the photo to choose what shows in the band.</p>
      {error && (
        <p className="fp-error" role="alert">
          {error}
        </p>
      )}
      <div className="fp-editor-foot">
        <span className="fp-grow" />
        <button type="button" className="fp-btn fp-btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="fp-btn" onClick={save} disabled={busy || !size}>
          {busy ? <Spinner /> : null}
          {busy ? "Uploading…" : "Use this cover"}
        </button>
      </div>
    </Modal>
  )
}

// Popover under "Change cover": pick a tone or pattern (previewed live in the
// band through onPreview), or upload a photo.
export const CoverPicker = ({ me, onPreview, onClose }) => {
  const current = resolveCover(me)
  const [picked, setPicked] = useState(current.style)
  const [image, setImage] = useState(null)
  const [fileError, setFileError] = useState("")
  const { save, busy, error } = useSaveSection()
  const fileInput = useRef(null)
  const box = useRef(null)

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !image) cancel()
    }
    const onDown = (e) => {
      if (!image && box.current && !box.current.contains(e.target) && !e.target.closest(".fp-cover-trigger")) cancel()
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onDown)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onDown)
    }
  }, [image])

  const pick = (style) => {
    setPicked(style)
    onPreview(style)
  }
  const cancel = () => {
    onPreview(null)
    onClose()
  }
  const done = async () => {
    if (picked === current.style) return cancel()
    if (await save("cover", { coverStyle: picked })) {
      onPreview(null)
      onClose()
    }
  }
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ""
    if (!file) return
    setFileError("")
    try {
      setImage(await prepareImage(file))
    } catch (err) {
      setFileError(err.message)
    }
  }

  return (
    <section className="fp-cover-picker" aria-labelledby="fp-cover-title" ref={box}>
      <div className="fp-row fp-row--between">
        <h2 className="fp-h3" id="fp-cover-title">
          Cover
        </h2>
        <span className="fp-muted">{(picked === "photo" ? { name: "Your photo" } : resolveCover({ ...me, coverStyle: picked })).name}</span>
      </div>
      <div className="fp-stack-sm">
        <h3 className="fp-label">Colors</h3>
        <div className="fp-swatches">
          {COVER_TONES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={classNames("fp-swatch", { "fp-on": picked === `tone:${t.id}` })}
              style={{ backgroundColor: t.color }}
              aria-label={t.name}
              aria-pressed={picked === `tone:${t.id}` ? "true" : "false"}
              onClick={() => pick(`tone:${t.id}`)}
            />
          ))}
        </div>
      </div>
      <div className="fp-stack-sm">
        <h3 className="fp-label">Patterns</h3>
        <div className="fp-patterns">
          {COVER_PATTERNS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={classNames("fp-swatch fp-swatch--pattern", { "fp-on": picked === `pattern:${p.id}` })}
              style={{ backgroundColor: p.base }}
              aria-label={p.name}
              aria-pressed={picked === `pattern:${p.id}` ? "true" : "false"}
              onClick={() => pick(`pattern:${p.id}`)}
            >
              <PatternArt id={p.id} />
            </button>
          ))}
        </div>
      </div>
      {me.coverPhoto && (
        <button
          type="button"
          className={classNames("fp-swatch fp-swatch--photo", { "fp-on": picked === "photo" })}
          aria-pressed={picked === "photo" ? "true" : "false"}
          onClick={() => pick("photo")}
        >
          <img src={me.coverPhoto} alt="" />
          <span>Your photo</span>
        </button>
      )}
      <div className="fp-stack-sm">
        <button type="button" className="fp-btn fp-btn--outline fp-btn--block" onClick={() => fileInput.current.click()}>
          <IconUpload />
          Upload a photo
        </button>
        <input ref={fileInput} type="file" accept="image/jpeg,image/png" className="fp-visually-hidden" onChange={onFile} tabIndex={-1} />
        <p className="fp-hint">Wide photos work best, at least 1500 pixels across.</p>
        {(fileError || error) && (
          <p className="fp-error" role="alert">
            {fileError || error}
          </p>
        )}
      </div>
      <div className="fp-editor-foot">
        <span className="fp-grow" />
        <button type="button" className="fp-btn fp-btn--ghost" onClick={cancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="fp-btn" onClick={done} disabled={busy}>
          {busy ? <Spinner /> : null}
          Save cover
        </button>
      </div>
      {image && (
        <CoverPhotoEditor
          image={image}
          onCancel={() => setImage(null)}
          onSaved={() => {
            setImage(null)
            onPreview(null)
            onClose()
          }}
        />
      )}
    </section>
  )
}

export const ChangeCoverButton = ({ onClick, compact }) => (
  <button type="button" className="fp-cover-trigger" onClick={onClick}>
    <IconImage />
    {compact ? "Cover" : "Change cover"}
  </button>
)
